import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getHindiLearning, deleteHindiLearning, createHindiLearning, updateHindiLearning } from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  { 
    key: "createdAt", 
    label: "Created",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const initialFormData = {
  title: "",
  description: "",
  hindiText: "",
  romanText: "",
  pronunciation: "",
  audioUrl: "",
  imageUrl: "",
  category: "Alphabet"
};

export default function HindiLearning() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || "",
      description: item.description || "",
      hindiText: item.hindiText || "",
      romanText: item.romanText || "",
      pronunciation: item.pronunciation || "",
      audioUrl: item.audioUrl || "",
      imageUrl: item.imageUrl || "",
      category: item.category || "Alphabet"
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (item) => {
    handleEdit(item);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && selectedItem) {
        await updateHindiLearning(selectedItem._id, formData);
        alert("Updated successfully!");
      } else {
        await createHindiLearning(formData);
        alert("Created successfully!");
      }
      setModalOpen(false);
      window.location.reload();
    } catch (error) {
      alert(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Hindi Learning"
          fetchData={getHindiLearning}
          deleteData={deleteHindiLearning}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          searchPlaceholder="Search hindi learning..."
        />
      </main>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Hindi Learning" : "Add New Hindi Learning"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter title"
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleFormChange}
              >
                <option value="Alphabet">Alphabet</option>
                <option value="Words">Words</option>
                <option value="Sentences">Sentences</option>
                <option value="Grammar">Grammar</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="2"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Hindi Content</h3>
            <div className="form-group">
              <label>Hindi Text</label>
              <textarea
                name="hindiText"
                value={formData.hindiText}
                onChange={handleFormChange}
                rows="2"
                placeholder="Enter Hindi text (देवनागरी)"
              />
            </div>
            <div className="form-group">
              <label>Roman Text</label>
              <input
                type="text"
                name="romanText"
                value={formData.romanText}
                onChange={handleFormChange}
                placeholder="Enter romanized text"
              />
            </div>
            <div className="form-group">
              <label>Pronunciation</label>
              <input
                type="text"
                name="pronunciation"
                value={formData.pronunciation}
                onChange={handleFormChange}
                placeholder="Enter pronunciation guide"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Media</h3>
            <div className="form-group">
              <label>Audio URL</label>
              <input
                type="url"
                name="audioUrl"
                value={formData.audioUrl}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update" : "Create")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
